/**
 * Document Template Schemas
 *
 * Zod validation schemas for document template management.
 * Issue #611 — Document Templates Settings Page (initial schema)
 * Issue #1474 — Unified Document/Termsheet templates page (UnifiedTemplate DTO,
 *               listUnified filter input, library-edit input shapes).
 */

import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

export const DocTemplateStatusSchema = z.enum(['active', 'archived']);
export type DocTemplateStatus = z.infer<typeof DocTemplateStatusSchema>;

/**
 * Issue #1474 — Unified status for the merged Document + Termsheet library.
 *
 * - `active`   — published, in use
 * - `draft`    — Termsheet only (UserTemplate w/ data.isDraft=true)
 * - `archived` — soft-deleted/hidden (Document.isActive=false)
 */
export const UnifiedTemplateStatusSchema = z.enum(['active', 'draft', 'archived']);
export type UnifiedTemplateStatus = z.infer<typeof UnifiedTemplateStatusSchema>;

/**
 * Issue #1474 — Discriminator for which underlying table a unified row came from.
 * Frontend renders type-aware columns/quick-views off of this.
 */
export const UnifiedTemplateTypeSchema = z.enum(['document', 'termsheet']);
export type UnifiedTemplateType = z.infer<typeof UnifiedTemplateTypeSchema>;

/**
 * Issue #1474 — Source flips between platform-seeded "system" rows and
 * business-owned "my" rows. Determines whether the row is editable
 * vs. only "Use as starting point".
 */
export const UnifiedTemplateSourceSchema = z.enum(['system', 'my']);
export type UnifiedTemplateSource = z.infer<typeof UnifiedTemplateSourceSchema>;

// ============================================================================
// UNIFIED DTO (Issue #1474)
// ============================================================================

/**
 * Unified frontend-facing row that merges DocumentTemplate (CRM) and
 * UserTemplate(TERMSHEET) (CRM) into one shape for the consolidated
 * /control-center/document-templates page.
 *
 * The merge happens server-side in `documentTemplates.listUnified`; the
 * frontend only sees this shape.
 */
export const UnifiedTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  type: UnifiedTemplateTypeSchema,
  source: UnifiedTemplateSourceSchema,
  tags: z.array(z.string()),
  category: z.string().nullable(),
  status: UnifiedTemplateStatusSchema,
  signerCount: z.number().int().nonnegative(),
  // Issue #1474 (PR review C3) — real DocuSeal field count surfaced so the
  // Document quick-view panel can render an actual number (not '?'), and
  // i18n pluralization receives a numeric `count` like every other stat.
  fieldCount: z.number().int().nonnegative(),
  usageCount: z.number().int().nonnegative(),
  updatedAt: z.string(), // ISO timestamp — last metadata write
  // Issue #1474 (PR review C4) — last DocuSeal sync timestamp; only set on
  // Document rows. Termsheet rows have no DocuSeal sync surface and leave
  // this null. The panel falls back to `updatedAt` when null.
  lastSyncedAt: z.string().nullable(),
});
export type UnifiedTemplate = z.infer<typeof UnifiedTemplateSchema>;

/**
 * Sort options for `listUnified`. Server-side sort to keep paging coherent.
 */
export const UnifiedTemplateSortSchema = z.enum(['updatedAt', 'name', 'usageCount']);
export type UnifiedTemplateSort = z.infer<typeof UnifiedTemplateSortSchema>;

export const UnifiedTemplateSortDirSchema = z.enum(['asc', 'desc']);
export type UnifiedTemplateSortDir = z.infer<typeof UnifiedTemplateSortDirSchema>;

/**
 * Filter input for `documentTemplates.listUnified`. All fields optional —
 * an empty object returns the full library.
 *
 * Issue #1474 (PR review C1): the unified list fans out to three sources
 * and merges in-process. Each branch is capped to keep the merge bounded;
 * the public `offset + limit` is also capped to {@link UNIFIED_TEMPLATES_PAGE_DEPTH_LIMIT}
 * so callers can't try to deep-paginate past what we actually fetch. UIs
 * that need deeper navigation should refine via `search` / `tags` / `category`
 * filters rather than scrolling — typical libraries fit comfortably.
 */
export const UNIFIED_TEMPLATES_PAGE_DEPTH_LIMIT = 1000;

export const ListUnifiedTemplatesInputSchema = z
  .object({
    type: UnifiedTemplateTypeSchema.optional(),
    source: UnifiedTemplateSourceSchema.optional(),
    // Multi-select tag filter; row matches if ANY tag is present (set overlap).
    tags: z.array(z.string().min(1).max(64)).max(32).optional(),
    category: z.string().min(1).max(64).optional(),
    status: UnifiedTemplateStatusSchema.optional(),
    search: z.string().max(200).optional(),
    sortBy: UnifiedTemplateSortSchema.default('updatedAt'),
    sortDir: UnifiedTemplateSortDirSchema.default('desc'),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
  })
  .refine((v) => v.offset + v.limit <= UNIFIED_TEMPLATES_PAGE_DEPTH_LIMIT, {
    message: `offset + limit must not exceed ${UNIFIED_TEMPLATES_PAGE_DEPTH_LIMIT}; refine via search/filter for deeper navigation`,
    path: ['offset'],
  });
export type ListUnifiedTemplatesInput = z.infer<typeof ListUnifiedTemplatesInputSchema>;

export const ListUnifiedTemplatesOutputSchema = z.object({
  rows: z.array(UnifiedTemplateSchema),
  total: z.number().int().nonnegative(),
  // Aggregated tag list across the visible filter scope, used to seed the
  // multi-select chip autocomplete in the FilterBar.
  availableTags: z.array(z.string()),
});
export type ListUnifiedTemplatesOutput = z.infer<typeof ListUnifiedTemplatesOutputSchema>;

// ============================================================================
// LIBRARY EDIT INPUTS (Issue #1474, mirrors termsheets.adhoc shape)
// ============================================================================

export const UploadDocumentTemplatePdfInputSchema = z.object({
  name: z.string().min(1).max(200),
  // Base64-encoded file body. Capped at 25 MiB after decoding (server-side check).
  fileBase64: z.string().min(1),
  fileName: z.string().min(1).max(255),
  category: z.string().min(1).max(64).optional(),
  tags: z.array(z.string().min(1).max(64)).max(32).optional(),
});
export type UploadDocumentTemplatePdfInput = z.infer<typeof UploadDocumentTemplatePdfInputSchema>;

export const StartDocumentTemplateDesignSessionInputSchema = z.object({
  documentTemplateId: z.string().min(1),
});
export type StartDocumentTemplateDesignSessionInput = z.infer<typeof StartDocumentTemplateDesignSessionInputSchema>;

export const GetDocumentTemplateEditorStateInputSchema = z.object({
  documentTemplateId: z.string().min(1),
});
export type GetDocumentTemplateEditorStateInput = z.infer<typeof GetDocumentTemplateEditorStateInputSchema>;

/**
 * Metadata-only update. DocuSeal autosaves field placement; this endpoint
 * persists name / tags / category / status changes from the frontend.
 */
export const UpdateDocumentTemplateInputSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  category: z.string().min(1).max(64).optional(),
  tags: z.array(z.string().min(1).max(64)).max(32).optional(),
  // `archived` flips isActive=false; `active` flips isActive=true. `draft` is
  // termsheet-only and rejected here at the router (DocumentTemplate has no draft).
  status: UnifiedTemplateStatusSchema.optional(),
});
export type UpdateDocumentTemplateInput = z.infer<typeof UpdateDocumentTemplateInputSchema>;

export const DuplicateDocumentTemplateInputSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(200).optional(),
});
export type DuplicateDocumentTemplateInput = z.infer<typeof DuplicateDocumentTemplateInputSchema>;

export const ArchiveDocumentTemplateInputSchema = z.object({
  id: z.string().min(1),
});
export type ArchiveDocumentTemplateInput = z.infer<typeof ArchiveDocumentTemplateInputSchema>;
